import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import { Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivationEnd, Event, NavigationStart, Router } from '@angular/router';
import { RouteData } from '@app/app.routing';
import { AnalyticsService } from '@app/core/analytics/analytics.service';
import { AppConfig } from '@app/core/app.config';
import { EventOriginEnum } from '@app/core/enums/analytics/analytics-value.enum';
import { PermissionEnum } from '@app/core/enums/permissions.enum';
import { UserRoleEnum } from '@app/core/enums/user-role-enum';
import { Document } from '@app/core/model/entities/document/document';
import { RetoolApp } from '@app/core/model/entities/retool/retool-app';
import { Notification } from '@app/core/model/other/notification';
import { AccessManager } from '@app/core/services/managers/access.manager';
import { AppManager } from '@app/core/services/managers/app.manager';
import { ActivationEndService } from '@app/features/main/activation-end.service';
import { SidenavService } from '@app/features/main/sidenav.service';
import { OrganizationLogoService } from '@app/features/main/views/dashboard/organization-logo.service';
import { PermissionAware } from '@app/shared/decorators/permissions-awareness.decorator';
import { AuthService } from '@auth0/auth0-angular';
import { environment } from '@env/environment';
import { TranslateService } from '@ngx-translate/core';
import { ChangeService } from '@services/change.service';
import { FileService } from '@services/file.service';
import { RetoolAppService } from '@services/retool-app.service';
import { merge, Observable, repeat, take } from 'rxjs';
import { filter, first, map, switchMap, tap } from 'rxjs/operators';

@Component({
    selector: 'menubar',
    templateUrl: './menubar.component.html',
    styleUrls: ['./menubar.component.scss'],
    standalone: false
})
@PermissionAware
export class MenubarComponent implements OnInit {
  @ViewChild('toolBarOrigin', {static: true}) protected toolBarRef: CdkOverlayOrigin = null;
  @ViewChild('rights') protected rightsTooltip: MatTooltip;
  @ViewChild('org') protected orgTooltip: MatTooltip;

  protected Permission = PermissionEnum;
  protected logo: string;
  protected data: RouteData;
  protected organizationLogo: Document;
  protected retoolApps$: Observable<RetoolApp[]>;
  protected notifications: Notification[];

  protected readonly UserRoleEnum = UserRoleEnum;

  protected homePage$: Observable<string>;

  protected readonly authService = inject(AuthService);
  protected readonly appConfig = inject(AppConfig);
  protected readonly appManager = inject(AppManager);
  protected readonly accessManager = inject(AccessManager);
  private readonly router = inject(Router);
  private readonly organizationLogoService = inject(OrganizationLogoService);
  private readonly sidenavService = inject(SidenavService);
  private readonly activationEndService = inject(ActivationEndService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly fileService = inject(FileService);
  private readonly translate = inject(TranslateService);
  private readonly changeService = inject(ChangeService);
  private readonly retoolAppService = inject(RetoolAppService);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {

    /*
     Subscribe to all events thrown by the Angular router through the common service
     */
    this.activationEndService.getRouteData()
      .pipe(
        filter((event: Event) => event instanceof NavigationStart),
        switchMap(() => {
          this.data = <RouteData>{};

          return this.router.events.pipe(
            filter((event: Event) => event instanceof ActivationEnd),
            filter((event: ActivationEnd) => event.snapshot.outlet === 'primary'),
            first()
          );
        }),
        switchMap(
          (activationEnd: ActivationEnd) => {
            // Make sure to overwrite the hideSidenav and hideHeader values with proper booleans
            this.data = {
              ...activationEnd.snapshot.data,
              hideHeader: !!activationEnd.snapshot.data.hideHeader,
              hideSidenav: !!activationEnd.snapshot.data.hideSidenav
            };

            //Manually add tooltips message if it wasn't set yet
            if (this.rightsTooltip && !this.rightsTooltip.message) {
              this.rightsTooltip.message = this.translate.instant('TOOLTIP.MANAGE_RIGHTS');
            }
            if (this.orgTooltip && !this.orgTooltip.message) {
              this.orgTooltip.message = this.translate.instant('TOOLTIP.CONFIGURATION');
            }

            // Get the organization current logo
            return this.organizationLogoService.loadOrganizationLogo()
              .pipe(
                tap((response) => {
                  this.organizationLogo = response;
                  this.setLogo(this.organizationLogo);
                })
              );
          }
        ),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe();
  }

  /**
   * Initialize Observables
   */
  public ngOnInit(): void {
    this.homePage$ = this.appManager.organization$.pipe(
      map(organization => organization ? `/organization/${organization.id}` : '/'),
      takeUntilDestroyed(this.destroyRef)
    );

    this.organizationLogoService.organizationLogo$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(logo => {
        this.organizationLogo = logo;
        this.setLogo(this.organizationLogo);
      });

    this.notifications = <Notification[]>[];

    this.changeService.entityChangedSubject.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.setLogo(this.organizationLogo);
    });

    this.retoolApps$ = this.appManager.organization$.pipe(
      filter(organization => !!organization),
      switchMap(() => this.retoolAppService.retoolApps$.pipe(
        // Refresh app list whenever an app is added, updated or deleted by the user or when the user hover the menu
        repeat({
          delay: () => merge(
            this.retoolAppService.retoolAppAdded$,
            this.retoolAppService.retoolAppUpdated$,
            this.retoolAppService.retoolAppDeleted$
          )
        })
        )
      ),
      takeUntilDestroyed(this.destroyRef)
    );
  }

  /**
   * Log out the current User then redirect back to myA home page.
   */
  public logout(): void {
    this.accessManager.logout().subscribe();
  }

  /**
   * Toggle the sidenav's state.
   */
  public toggleSideNav(): void {
    this.sidenavService.toggleSidenav();
  }

  public onNavigation(navigateTo: string): void {
    this.analyticsService.trackNavigationEvent(EventOriginEnum.MENUBAR, navigateTo);
  }

  public get organizationName(): string {
    if (this.appManager.currentOrganization) {
      return this.appManager.currentOrganization.name;
    }
    return '';
  }

  public setLogo(document: Document): void {
    if (this.organizationLogo) {
      this.fileService.getDocumentFileUrl(document.id).subscribe((documentUrl) => {
        this.logo = documentUrl;
      });
    } else {
      this.logo = null;
    }
  }

  /**
   * Open a new tab and navigate to the URL for the tbmaestro accounts profile page.
   */
  public openProfilePage(): void {
    this.onNavigation('MENU.MY_PROFILE');

    this.authService.user$.pipe(take(1))
      .subscribe(user => {
        const url = new URL('profile', environment.profile.accountsPortal);
        url.searchParams.append('login_hint', user.email);
        window.open(url, '_blank').focus();
      });
  }
}
